import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap, map, catchError } from 'rxjs/operators';
import { environment } from '@env/environment';
import { GracePeriodFeature } from '@domains/admin/models/feature-admin.model';

interface FeaturesResponse {
  plan: string;
  features: Record<string, boolean>;
  gracePeriod?: Record<string, GracePeriodFeature>;
}

/**
 * Signal-based service that loads and caches the feature map for the
 * authenticated organization. Provides reactive access to feature flags
 * for conditional rendering and route guarding.
 *
 * On load failure, defaults to allowing all features (graceful degradation).
 */
@Injectable({ providedIn: 'root' })
export class FeatureService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.authAPI}/features`;
  private readonly superAdmin = signal(false);

  private readonly features = signal<Record<string, boolean>>({});
  private readonly currentPlan = signal<string>('basic');
  private readonly featuresLoaded = signal<boolean>(false);
  private readonly gracePeriodMap = signal<Record<string, GracePeriodFeature>>({});
  private sessionCleared = false;

  /** Read-only snapshot of the current feature map. */
  readonly featureMap = this.features.asReadonly();

  /** Current plan identifier (e.g. 'basic', 'pro', 'enterprise'). */
  readonly plan = this.currentPlan.asReadonly();

  /** Whether the features have been loaded at least once. */
  readonly loaded = this.featuresLoaded.asReadonly();

  /** Read-only snapshot of features currently in grace period. */
  readonly gracePeriod = this.gracePeriodMap.asReadonly();

  /** Whether any feature is currently in grace period. */
  readonly hasGracePeriod = computed(() => Object.keys(this.gracePeriodMap()).length > 0);

  /** Days remaining until the nearest grace period expires. */
  readonly gracePeriodDaysLeft = computed(() => {
    const entries = Object.values(this.gracePeriodMap());
    if (entries.length === 0) return 0;
    const nearest = Math.min(...entries.map(e => new Date(e.expiresAt).getTime()));
    return Math.max(0, Math.ceil((nearest - Date.now()) / 86_400_000));
  });

  /** Sets the super_admin bypass flag. Called by AuthService after login. */
  setSuperAdmin(value: boolean): void {
    this.superAdmin.set(value);
    this.sessionCleared = false;
  }

  /**
   * Fetches the feature map from the BFF and caches it in signals.
   * Skips the request if the session was cleared (logout). On error
   * falls back to allowing all features (graceful degradation).
   */
  loadFeatures(): Observable<void> {
    if (this.sessionCleared) return of(undefined);
    return this.http.get<FeaturesResponse>(`${this.apiUrl}/me`).pipe(
      tap(response => {
        this.features.set(response.features);
        this.currentPlan.set(response.plan);
        this.gracePeriodMap.set(response.gracePeriod ?? {});
        this.featuresLoaded.set(true);
      }),
      map(() => undefined),
      catchError(() => {
        this.featuresLoaded.set(true);
        return of(undefined);
      })
    );
  }

  /**
   * Returns whether a single feature is enabled for the current org.
   * Returns `false` if features have not loaded yet or the key is absent.
   * The `featureGuard` waits for features to load before calling this method.
   */
  isEnabled(featureId: string): boolean {
    if (this.superAdmin()) return true;
    const map = this.features();
    if (!this.featuresLoaded()) return false;
    if (!(featureId in map)) return false;
    return map[featureId];
  }

  /** Returns `true` if at least one of the given feature IDs is enabled. */
  anyEnabled(featureIds: string[]): boolean {
    return featureIds.some(id => this.isEnabled(id));
  }

  /** Whether a feature is in grace period (enabled but soon to be disabled). */
  isInGracePeriod(featureId: string): boolean {
    return featureId in this.gracePeriodMap();
  }

  /** Grace period expiry date for a feature, or null if not in grace period. */
  gracePeriodExpiry(featureId: string): Date | null {
    const info = this.gracePeriodMap()[featureId];
    return info ? new Date(info.expiresAt) : null;
  }

  /** Clears the cached feature map. Should be called on logout. */
  clear(): void {
    this.features.set({});
    this.currentPlan.set('basic');
    this.featuresLoaded.set(false);
    this.superAdmin.set(false);
    this.gracePeriodMap.set({});
    this.sessionCleared = true;
  }
}
